import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { createImageVariantConfig } from '../../../util/sdkLoader';
import { isErrorUserPendingApproval, isForbiddenError, storableError } from '../../../util/errors';
import { isOriginInUse } from '../../../util/search';
import { hasPermissionToViewData, isUserAuthorized } from '../../../util/userHelpers';
import { parse } from '../../../util/urlHelpers';
import { addMarketplaceEntities } from '../../../ducks/marketplaceData.duck';

// Pagination page size
const RESULT_PAGE_SIZE = 24;

// ================ Helper Functions ================ //

const resultIds = data => {
    const listings = data.data;
    return listings
        .filter(l => !l.attributes.deleted && l.attributes.state === 'published')
        .map(l => l.id);
};

// ================ Async Thunks ================ //

/////////////////////
// Search Listings //
/////////////////////
const searchListingsPayloadCreator = ({ searchParams, config }, thunkAPI) => {
    const { dispatch, rejectWithValue, extra: sdk } = thunkAPI;

    const params = {
        ...searchParams,
        perPage: searchParams.perPage || RESULT_PAGE_SIZE,
    };

    return sdk.listings
        .query(params)
        .then(response => {
            const listingFields = config?.listing?.listingFields;
            const sanitizeConfig = { listingFields };

            dispatch(addMarketplaceEntities(response, sanitizeConfig));
            return response;
        })
        .catch(e => {
            const error = storableError(e);
            if (!(isErrorUserPendingApproval(error) || isForbiddenError(error))) {
                return rejectWithValue(error);
            }
            return rejectWithValue(error);
        });
};

export const searchListings = createAsyncThunk(
    'MarketplacePage/searchListings',
    searchListingsPayloadCreator
);

// ================ Slice ================ //

const marketplacePageSlice = createSlice({
    name: 'MarketplacePage',
    initialState: {
        pagination: null,
        searchParams: null,
        searchInProgress: false,
        searchListingsError: null,
        currentPageResultIds: [],
    },
    reducers: {},
    extraReducers: builder => {
        // Search Listings
        builder
            .addCase(searchListings.pending, (state, action) => {
                state.searchParams = action.meta.arg.searchParams;
                state.searchInProgress = true;
                state.searchListingsError = null;
            })
            .addCase(searchListings.fulfilled, (state, action) => {
                state.currentPageResultIds = resultIds(action.payload.data);
                state.pagination = action.payload.data.meta;
                state.searchInProgress = false;
            })
            .addCase(searchListings.rejected, (state, action) => {
                // eslint-disable-next-line no-console
                console.error(action.payload);
                state.searchInProgress = false;
                state.searchListingsError = action.payload;
            });
    },
});

export default marketplacePageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
    // In private marketplace mode, this page won't fetch data if the user is unauthorized
    const state = getState();
    const currentUser = state.user?.currentUser;
    const isAuthorized = currentUser && isUserAuthorized(currentUser);
    const hasViewingRights = currentUser && hasPermissionToViewData(currentUser);
    const isPrivateMarketplace = config.accessControl.marketplace.private === true;
    const canFetchData =
        !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized && hasViewingRights);

    if (!canFetchData) {
        console.warn('MarketplacePage - User does not have permission to view data');
        return Promise.resolve();
    }

    const queryParams = parse(search, {
        latlng: ['origin'],
        latlngBounds: ['bounds'],
    });

    const { page = 1, address, origin, ...rest } = queryParams;
    const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

    const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
    } = config.layout.listingImage;
    const aspectRatio = aspectHeight / aspectWidth;

    const searchListingsCall = searchListings({
        searchParams: {
            ...rest,
            ...originMaybe,
            page,
            perPage: RESULT_PAGE_SIZE,
            include: ['author', 'images'],
            'fields.listing': [
                'title',
                'geolocation',
                'price',
                'deleted',
                'state',
                'publicData.listingType',
                'publicData.transactionProcessAlias',
                'publicData.unitType',
                'publicData.cardStyle',
                'publicData.location',
                'publicData.pickupEnabled',
                'publicData.shippingEnabled',
                'publicData.priceVariationsEnabled',
                'publicData.priceVariants',
                'publicData'
            ],
            'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
            'fields.image': [
                'variants.scaled-small',
                'variants.scaled-medium',
                `variants.${variantPrefix}`,
                `variants.${variantPrefix}-2x`,
            ],
            ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
            ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
            'limit.images': 1,
        },
        config,
    });

    return dispatch(searchListingsCall);
};
